/* eslint-disable jsx-a11y/no-static-element-interactions */
import "./QuickNews.css";

import { HTTP_STATUS } from "@api/constant";
import {
  renderFeedCard,
  renderVideoCard,
} from "@components/Layouts/QuickNews/QnCardHelper";
import QnCardNav from "@components/Layouts/QuickNews/QnCardNav";
import QnFooter from "@components/Layouts/QuickNews/QnFooter";
import QnOnboard from "@components/Layouts/QuickNews/QnOnboard";
import QnSideNav from "@components/Layouts/QuickNews/QnSideNav";
import QnTopNav from "@components/Layouts/QuickNews/QnTopNav";
import { LOCALHOST } from "@config/MetaTagsConstants";
import { QN_ANALYTICS_EVENT_BASE } from "@config/QuickNewsConstants";
import QuickNewsContainer, { AdsSettings } from "@container/QuickNewsContainer";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { formatDate } from "@helper/useDateFormat";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { Route } from "@route-types/QuickNews/+types/QuickNews";
import { getAuthorsName } from "@routes/Article/ArticleMetaData";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { QuickNewsDataPayload } from "@typings/PagePayloadType";
import { FeedItem, QNCardType, VideoItem } from "@typings/QuickNews";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { getCardStyle } from "./helper/QnCardStyle";
import {
  useInfiniteContent,
  useInfiniteVideos,
} from "./helper/QuickNewsHelper";
import { fetchQNData } from "./QuickNews.server";
import QuickNewsMetaData from "./QuickNewsMetaData";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    QuickNewsDataPayload,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function QuickNews() {
  const dataLoaderResponse =
    useLoaderData<TRouteWithRedirect<QuickNewsDataPayload, string>>();

  if (dataLoaderResponse.type !== ResponseType.SUCCESS)
    throw new Error("Something went wrong");

  const TmCustomEvent = useTMCustomEventAnalyticsCall();

  const sectionName = "quick-news";
  const adSettings: AdsSettings = useGlobalAdSetting();
  const pageAdTargetValue = PageAdTargetingTypeEnum.HOME;

  const Stories = dataLoaderResponse.payload.recentArticles;
  const photoCards = useMemo(
    () => dataLoaderResponse.payload.photoCards || [],
    [dataLoaderResponse.payload.photoCards],
  );
  const bcVideos = dataLoaderResponse.payload.bcVideos;
  const initialActiveTab = dataLoaderResponse.payload.initialActiveTab;
  const initialId = dataLoaderResponse.payload.initialId;

  const {
    content: articles,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  } = useInfiniteContent({ Stories, photoCards, initialActiveTab, initialId });
  const { videos, hasMoreVideos, loadMoreVideos } = useInfiniteVideos({
    bcVideos,
    initialId,
    initialActiveTab,
  });

  const [activeTab, setActiveTab] = useState<QNCardType>(initialActiveTab);
  const [articleIndex, setArticleIndex] = useState(0);
  const [videoIndex, setVideoIndex] = useState(0);
  const [isMute, setIsMute] = useState(false);

  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const [onBoarded, setOnBoarded] = useState(!!initialId);

  const cards = activeTab === "MyFeed" ? articles : videos;
  const currentIndex = activeTab === "MyFeed" ? articleIndex : videoIndex;
  const setCurrentIndex =
    activeTab === "MyFeed" ? setArticleIndex : setVideoIndex;

  // Responsive
  useEffect(() => {
    setMounted(true);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Prevent pull-to-refresh and iOS bounce
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) return; // Allow multi-touch gestures
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventDefault, { passive: false });
    return () => {
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overscrollBehavior = "";
      document.removeEventListener("touchmove", preventDefault);
    };
  }, [isMobile]);

  // Infinite content loading
  useEffect(() => {
    const fetchMoreFeed = async () => {
      if (articles.length - articleIndex === 3 && hasMore && isInitialized)
        await loadMore();
    };
    fetchMoreFeed().catch(console.error);
  }, [articleIndex, articles.length, loadMore, hasMore, isInitialized]);

  const getOlderArticles = useCallback(() => {
    loadMoreOlder();
    void loadMore();
  }, [loadMoreOlder, loadMore]);

  useEffect(() => {
    const fetchMoreVideos = async () => {
      if (videos.length - videoIndex === 3 && hasMoreVideos && isInitialized)
        await loadMoreVideos();
    };
    fetchMoreVideos().catch(console.error);
  }, [videoIndex, videos.length, loadMoreVideos, hasMoreVideos, isInitialized]);

  // Drag/Touch state
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startX, setStartX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);

  const getEventLabel = useCallback(
    (card: FeedItem | VideoItem) =>
      "title" in card ? card.title : "name" in card ? card.name : "",
    [],
  );

  const setMetaTags = useCallback(
    (index?: number, tab?: string) => {
      const currentCardIndex = index ?? currentIndex;
      const currentActiveTab = tab ?? activeTab;
      const card =
        currentActiveTab == "MyFeed"
          ? articles[currentCardIndex]
          : videos[currentCardIndex];
      const { metaTags } = QuickNewsMetaData({
        card,
        activeTab: currentActiveTab,
        onBoarded: true,
        pagination: currentCardIndex,
      });

      document.title = metaTags.title || "";
      const metaDescription = document.querySelector<HTMLMetaElement>(
        'meta[name="description"]',
      );

      if (metaDescription) {
        metaDescription.content = metaTags.description || "";
      }

      const metaCanonical = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (metaCanonical) {
        metaCanonical.href = `${window.location.origin}/${metaTags.slug || ""}`;
      }
    },
    [currentIndex, activeTab, articles, videos],
  );

  const setPushState = useCallback(
    (tab: QNCardType, activeIndex?: number) => {
      const index = activeIndex ?? currentIndex;
      if (
        tab === "MyFeed" &&
        articles[index] &&
        !("adType" in articles[index] || "endCard" in articles[index])
      ) {
        const item = articles[index];
        window.history.pushState(
          "article",
          item.title,
          `/quick-news/${item.id}`,
        );
        setOnBoarded(true);
      } else if (
        tab === "Videos" &&
        videos[index] &&
        !("endCard" in videos[index])
      ) {
        const item = videos[index];
        window.history.pushState(
          "video",
          item.name,
          `/quick-news/video/${item.id}`,
        );
        setOnBoarded(true);
      }
    },
    [currentIndex, articles, videos],
  );

  const switchTab = useCallback(
    (tab: QNCardType, eventType?: string) => {
      setActiveTab(tab);
      setPushState(tab);
      setMetaTags(undefined, tab);
      const eventActionMap: Record<string, string> = {
        keyboard: "keyboard press",
        mouse: "mouse drag",
        touch: "touch swipe",
      };
      const eventAction = eventActionMap[eventType || ""] || "click";
      const platform = eventType === "touch" ? "mobile" : "desktop";
      const nextIndex = tab === "MyFeed" ? articleIndex + 1 : videoIndex + 1;
      TmCustomEvent({
        ...QN_ANALYTICS_EVENT_BASE,
        eventAction: `${platform}::${eventAction}::${nextIndex}`,
        eventLabel: tab,
      });
    },
    [setPushState, setMetaTags, articleIndex, videoIndex, TmCustomEvent],
  );

  const trackCardNavigation = useCallback(
    (direction: "previous" | "next", eventType?: string) => {
      const eventActionMap: Record<string, string> = {
        keyboard: "keyboard press",
        scroll: "mouse scroll",
        mouse: "mouse drag",
        touch: "touch swipe",
      };
      const eventAction = eventActionMap[eventType || ""] || "click";
      const platform = eventType === "touch" ? "mobile" : "desktop";
      const nextIndex =
        activeTab === "MyFeed" ? articleIndex + 1 : videoIndex + 1;
      const currentTypeIndex =
        direction === "next" ? nextIndex : currentIndex - 1;
      const currentCard =
        activeTab === "MyFeed"
          ? articles[currentTypeIndex]
          : videos[currentTypeIndex];
      TmCustomEvent({
        ...QN_ANALYTICS_EVENT_BASE,
        eventAction: `${platform}::${eventAction} ${direction}::${direction == "next" ? currentTypeIndex : currentTypeIndex + 1}`,
        eventLabel: getEventLabel(currentCard),
      });

      if (activeTab === "MyFeed" && !("adType" in articles[currentTypeIndex])) {
        TmCustomEvent({
          event: "virtual_pv",
          articleid: !("quizzes" in articles[currentTypeIndex])
            ? articles[currentTypeIndex].id
            : "",
          cue_articleid: !("quizzes" in articles[currentTypeIndex])
            ? articles[currentTypeIndex].id
            : "",
          pagination: currentTypeIndex + 1,
          author:
            "authors" in articles[currentTypeIndex]
              ? getAuthorsName(articles[currentTypeIndex].authors || [])
              : "",
          level2: "quick-news",
          chapter1: "",
          contentcat: 1,
          contenttype: undefined,
          pubdate:
            "datetime" in articles[currentTypeIndex]
              ? formatDate(
                  articles[currentTypeIndex].datetime,
                  "yyyy-MM-dd HH:mm:ss",
                )
              : "",
          title: articles[currentTypeIndex].title,
          virtual_url:
            articles[currentTypeIndex].id == "tamil-quiz"
              ? "quick-news/word-quiz"
              : `quick-news/${articles[currentTypeIndex].id}`,
        });
      }
    },
    [
      activeTab,
      articleIndex,
      videoIndex,
      currentIndex,
      articles,
      videos,
      getEventLabel,
      TmCustomEvent,
    ],
  );

  const prevCard = useCallback(
    (eventType?: string) => {
      if (
        (activeTab === "MyFeed" && articleIndex === 0) ||
        (activeTab === "Videos" && videoIndex === 0)
      ) {
        window.location.href = "/quick-news";
        setOnBoarded(false);
        return;
      }

      setCurrentIndex((prev) => prev - 1);
      trackCardNavigation("previous", eventType);
      setPushState(activeTab, currentIndex - 1);
      setMetaTags(currentIndex - 1);
    },
    [
      activeTab,
      articleIndex,
      videoIndex,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  const nextCard = useCallback(
    (eventType?: string) => {
      if (
        (activeTab === "MyFeed" && articleIndex >= articles.length - 1) ||
        (activeTab === "Videos" && videoIndex >= videos.length - 1)
      )
        return;
      setCurrentIndex((prev) => prev + 1);
      trackCardNavigation("next", eventType);
      setPushState(activeTab, currentIndex + 1);
      setMetaTags(currentIndex + 1);
    },
    [
      activeTab,
      articleIndex,
      articles.length,
      videoIndex,
      videos.length,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        currentIndex < cards.length - 1 && onBoarded && nextCard("keyboard");
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        currentIndex > 0 && onBoarded && prevCard("keyboard");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onBoarded && switchTab("MyFeed", "keyboard");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onBoarded && switchTab("Videos", "keyboard");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, cards.length, prevCard, nextCard, switchTab, onBoarded]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (isScrolling.current) return;
      isScrolling.current = true;
      if (e.deltaY > 0 && currentIndex < cards.length - 1) nextCard("scroll");
      else if (e.deltaY < 0 && currentIndex > 0) prevCard("scroll");
      setTimeout(() => (isScrolling.current = false), 200);
    };
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [currentIndex, cards.length, prevCard, nextCard]);

  const handleStart = (clientY: number, clientX: number) => {
    setIsDragging(true);
    setStartY(clientY);
    setStartX(clientX);
  };
  const handleMove = (clientY: number, clientX: number) => {
    if (!isDragging) return;
    setOffsetY(clientY - startY);
    setOffsetX(clientX - startX);
  };

  const handleEnd = useCallback(
    (eventHandler: string) => {
      setIsDragging(false);
      const horizontalThreshold = 50,
        verticalThreshold = 30;
      if (
        Math.abs(offsetX) > Math.abs(offsetY) &&
        Math.abs(offsetX) > horizontalThreshold
      )
        offsetX > 0
          ? switchTab("MyFeed", eventHandler)
          : switchTab("Videos", eventHandler);
      else if (Math.abs(offsetY) > Math.abs(offsetX))
        offsetY > verticalThreshold
          ? currentIndex === 0
            ? (window.location.href = "/quick-news")
            : prevCard(eventHandler)
          : offsetY < -verticalThreshold &&
            currentIndex < cards.length - 1 &&
            nextCard(eventHandler);
      setOffsetY(0);
      setOffsetX(0);
    },
    [
      offsetX,
      offsetY,
      currentIndex,
      cards.length,
      prevCard,
      nextCard,
      switchTab,
    ],
  );

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) handleEnd("mouse");
    };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, handleEnd]);

  return (
    <QuickNewsContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
    >
      <QnOnboard
        isMobile={isMobile}
        activeTab={activeTab}
        initialId={initialId}
        setPushState={setPushState}
        article={
          activeTab === "MyFeed"
            ? (articles[0] as ProcessedArticleData)
            : undefined
        }
      />
      <QnTopNav activeTab={activeTab} setActiveTab={switchTab} />
      <div className="side-nav">
        <QnSideNav activeTab={activeTab} setActiveTab={switchTab} />
      </div>

      <div
        ref={containerRef}
        className="qn-card-container relative w-full h-[calc(100svh-90px)] px-2.5 overflow-hidden cursor-grab active:cursor-grabbing select-none mt-4"
        style={{ touchAction: "none", overscrollBehavior: "none" }}
        onMouseDown={(e) => {
          handleStart(e.clientY, e.clientX);
        }}
        onMouseMove={(e) => {
          handleMove(e.clientY, e.clientX);
        }}
        onMouseUp={() => {
          handleEnd("mouse");
        }}
        onMouseLeave={() => {
          handleEnd("mouse");
        }}
        onTouchStart={(e) => {
          handleStart(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchMove={(e) => {
          handleMove(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchEnd={() => {
          handleEnd("touch");
        }}
      >
        <div className="qn-card-wrapper relative w-full md:w-[450px] h-full mx-auto">
          {articles.map((card, index) => (
            <div
              key={`qn-article-${card.id}`}
              className="absolute inset-0 flex items-center justify-center h-full md:h-[80svh]"
              style={{
                ...getCardStyle(
                  index,
                  "MyFeed",
                  activeTab === "MyFeed",
                  articleIndex,
                  videoIndex,
                  isDragging,
                  offsetY,
                  isMobile,
                  containerRef,
                ),
                transition:
                  isDragging && activeTab === "MyFeed"
                    ? "none"
                    : "all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.175)",
                pointerEvents:
                  index === articleIndex && activeTab === "MyFeed"
                    ? "auto"
                    : "none",
              }}
            >
              <div className="w-full h-full rounded-3xl flex flex-col items-center justify-center overflow-hidden">
                {mounted &&
                  renderFeedCard({
                    card,
                    index,
                    isMobile,
                    articleIndex,
                    switchTab,
                    prevCard,
                    nextCard,
                    getOlderArticles,
                  })}
              </div>
            </div>
          ))}

          {videos.map((card, index) => (
            <div
              key={`qn-video-${card.id}`}
              className="absolute inset-0 flex items-center justify-center h-full md:h-[80svh]"
              style={{
                ...getCardStyle(
                  index,
                  "Videos",
                  activeTab === "Videos",
                  articleIndex,
                  videoIndex,
                  isDragging,
                  offsetY,
                  isMobile,
                  containerRef,
                ),
                transition:
                  isDragging && activeTab === "Videos"
                    ? "none"
                    : "all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.175)",
                pointerEvents:
                  index === videoIndex && activeTab === "Videos"
                    ? "auto"
                    : "none",
              }}
            >
              <div className="w-full h-full rounded-3xl flex flex-col items-center justify-center overflow-hidden">
                {mounted &&
                  renderVideoCard({
                    card,
                    index,
                    isMute,
                    setIsMute,
                    activeTab,
                    videoIndex,
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <QnCardNav
        activeTab={activeTab}
        canPrevFeed={articleIndex > 0}
        canPrevVideo={videoIndex > 0}
        canNextFeed={articleIndex < articles.length - 1}
        canNextVideo={videoIndex < videos.length - 1}
        onPrev={prevCard}
        onNext={nextCard}
      />
      <QnFooter />
    </QuickNewsContainer>
  );
}

export async function loader({ params, request }: Route.LoaderArgs) {
  const url = new URL(request.url, LOCALHOST);
  const pathname = url.pathname;

  let data: Awaited<ReturnType<typeof fetchQNData>> | null = null;

  if (params.vid && pathname.includes("video")) {
    data = await fetchQNData({ vid: params.vid });
  } else if (params.id) {
    data = await fetchQNData({ id: params.id });
  } else {
    data = await fetchQNData();
  }

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };

  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<QuickNewsDataPayload, string>
  >;
}
